/**
 * HTTP-backed {@link SimulationApi} implementation for playground mode.
 *
 * Uses HTTP calls to Cloud Functions for all operations.
 * Real-time subscriptions use Firestore listeners.
 *
 * ⚠️ PLAYGROUND-ONLY: This file depends on Firebase and should ONLY be imported by PlaygroundHost.
 * Do NOT import from barrel exports (simulation/index.ts) - use direct import path instead.
 * This ensures Firebase is not bundled in production builds.
 */
import {
  CollectRecipeResult,
  ExecuteRecipeOptions,
  ExecuteRecipeResponse,
  ExecuteScopedRecipeResult,
  ExecuteScopedRecipeOptions,
  GetActiveRunsOptions,
  GetAvailableRecipesOptions,
  GetAvailableRecipesResult,
  GetBatchRecipeRequirements,
  Recipe,
  SimulationApi,
  SimulationAvailableItem,
  SimulationAssignment,
  SimulationBatchOperation,
  SimulationBatchOperationsResult,
  SimulationPowerPreview,
  SimulationSlotMutationResult,
  SimulationSlotValidationResult,
  SimulationSlotContainer,
  SimulationRunSummary,
  SimulationState,
  TriggerRecipeChainOptions,
  ResetStateOptions,
  ResetStateResult,
} from './SimulationApi'
import { callRemoteFunction, type RemoteFunctionInvoker } from '../http/callRemoteFunction'
import {
  RecipeRequirementResult,
  RundotGameSimulationConfig,
  SimulationSubscribeOptions,
  SimulationSnapshotUpdate,
} from '../rundot-game-api/types'
import {
  observeFirestoreDocument,
  observeFirestoreCollection,
} from '../firebase/firebaseClient'

/**
 * Firestore document structure for simulation state.
 */
interface FirestoreSimulationState {
  inventory?: Record<string, number | string>
  disabledRecipes?: string[]
  sharedAssets?: Record<string, unknown>
  lastUpdated?: unknown
}

/**
 * Firestore document structure for simulation runs.
 */
interface FirestoreSimulationRun {
  id: string
  recipeId: string
  status: string
  startTime: unknown
  expiresAt: unknown
  entity?: string
  inputs?: Record<string, number | string>
  outputs?: Record<string, number | string>
  profileId: string
  appId: string
  roomId?: string
}

/**
 * HTTP-based implementation of SimulationApi for playground mode.
 */
export class HttpSimulationApi implements SimulationApi {
  private readonly appId: string
  private _simulationConfig: RundotGameSimulationConfig | null = null
  private readonly callRemote: RemoteFunctionInvoker
  private readonly getProfileId: () => string
  private readonly versionTag?: string
  private readonly activeSubscriptions: Set<() => void> = new Set()

  constructor(
    appId: string,
    options: {
      callRemote?: RemoteFunctionInvoker
      getProfileId: () => string
      versionTag?: string
    },
  ) {
    this.appId = appId
    this.callRemote = options.callRemote ?? callRemoteFunction
    this.getProfileId = options.getProfileId
    this.versionTag = options.versionTag
  }

  /**
   * Clear the cached simulation config.
   * Called after a server config re-upload to force fresh retrieval.
   */
  clearConfigCache(): void {
    this._simulationConfig = null
  }

  isEnabled(): boolean {
    return true
  }

  // ===== SUBSCRIPTION (FIRESTORE) =====

  async subscribeAsync(
    options: SimulationSubscribeOptions,
  ): Promise<() => void> {
    this.ensureValidSubscribeOptions(options)

    const roomId = options.roomId
    const unsubscribers: Array<() => void> = []

    let lastInventorySignature: string | null = null
    let lastRunsSignature: string | null = null
    let latestInventory: Record<string, number | string> = {}
    let latestRuns: SimulationRunSummary[] = []

    const requestedEntityIds = Array.isArray(options.entities)
      ? options.entities.filter((e): e is string => typeof e === 'string' && e.length > 0)
      : []

    const requestedTags = Array.isArray(options.tags)
      ? options.tags.filter((t): t is string => typeof t === 'string' && t.length > 0)
      : []

    const wantsInventory = requestedEntityIds.length > 0 || requestedTags.length > 0
    const wantsActiveRuns = Boolean(options.activeRuns)

    const cleanupPartialSubscriptions = (): void => {
      for (const unsub of unsubscribers) {
        try {
          unsub()
        } catch (e) {
          console.error('[RUN] Error during simulation subscription cleanup:', e)
        }
      }
    }

    const sendUpdate = (): void => {
      const snapshot: SimulationSnapshotUpdate = {
        type: 'snapshot',
        timestamp: Date.now(),
      }
      if (wantsInventory) {
        snapshot.entities = Object.entries(latestInventory).map(([entityId, quantity]) => ({
          entityId,
          quantity,
        }))
      }
      if (wantsActiveRuns) {
        snapshot.activeRuns = latestRuns
      }
      options.onUpdate(snapshot)
    }

    try {
      let allowedEntityIds: Set<string> | null = null
      if (wantsInventory) {
        allowedEntityIds = new Set<string>()
        for (const id of requestedEntityIds) {
          allowedEntityIds.add(id)
        }

        if (requestedTags.length > 0) {
          const config = await this.getConfigAsync(roomId)
          const requestedTagSet = new Set(requestedTags)
          for (const [entityId, entityDef] of Object.entries(config.entities || {})) {
            const tags = entityDef?.tags || []
            if (Array.isArray(tags) && tags.some((t) => requestedTagSet.has(t))) {
              allowedEntityIds.add(entityId)
            }
          }
        }

        // Subscribe to state document (inventory) only when inventory is requested.
        const statePath = roomId
          ? `h5_rooms/${roomId}/h5_simulation/${this.appId}`
          : `profiles/${this.getProfileId()}/h5_simulation/${this.appId}`

        const unsubscribeState = await observeFirestoreDocument<FirestoreSimulationState>(
          statePath,
          (doc) => {
            const inventory = doc?.inventory ?? {}
            const filteredInventory: Record<string, number | string> = {}

            for (const [entityId, quantity] of Object.entries(inventory)) {
              if (!allowedEntityIds || allowedEntityIds.has(entityId)) {
                filteredInventory[entityId] = quantity
              }
            }

            const signature = JSON.stringify(filteredInventory)
            if (signature !== lastInventorySignature) {
              lastInventorySignature = signature
              latestInventory = filteredInventory
              sendUpdate()
            }
          },
        )
        unsubscribers.push(unsubscribeState)
      }

      // Subscribe to active runs if requested
      if (wantsActiveRuns) {
        const filters = roomId
          ? [
              { field: 'roomId', op: '==' as const, value: roomId },
              { field: 'appId', op: '==' as const, value: this.appId },
              { field: 'status', op: 'in' as const, value: ['running', 'awaiting_collection'] },
            ]
          : [
              { field: 'profileId', op: '==' as const, value: this.getProfileId() },
              { field: 'appId', op: '==' as const, value: this.appId },
              { field: 'status', op: 'in' as const, value: ['running', 'awaiting_collection'] },
            ]

        const unsubscribeRuns = await observeFirestoreCollection<FirestoreSimulationRun>(
          'h5_simulation_runs',
          {
            filters,
            orderBy: [{ field: 'expiresAt', direction: 'desc' }],
          },
          (docs) => {
            const runs: SimulationRunSummary[] = docs.map((doc) => ({
              id: doc.id,
              recipeId: doc.recipeId,
              status: doc.status as 'running' | 'awaiting_collection',
              startTime: normalizeTimestamp(doc.startTime),
              expiresAt: normalizeTimestamp(doc.expiresAt),
              entity: doc.entity,
              inputs: doc.inputs,
              outputs: doc.outputs,
            }))

            const signature = JSON.stringify(runs)
            if (signature !== lastRunsSignature) {
              lastRunsSignature = signature
              latestRuns = runs
              sendUpdate()
            }
          },
        )
        unsubscribers.push(unsubscribeRuns)
      }
    } catch (error) {
      cleanupPartialSubscriptions()
      throw error
    }

    const unsubscribe = (): void => {
      for (const unsub of unsubscribers) {
        try {
          unsub()
        } catch (e) {
          console.error('[RUN] Error during simulation subscription cleanup:', e)
        }
      }
      this.activeSubscriptions.delete(unsubscribe)
    }

    this.activeSubscriptions.add(unsubscribe)
    return unsubscribe
  }

  // ===== STATE & CONFIG (HTTP) =====

  async getStateAsync(roomId?: string): Promise<SimulationState> {
    return this.callRemote<SimulationState>('h5/simulation/state', this.basePayload({ roomId }))
  }

  async getConfigAsync(roomId?: string): Promise<RundotGameSimulationConfig> {
    if (this._simulationConfig) {
      return this._simulationConfig
    }

    const config = await this.callRemote<RundotGameSimulationConfig>(
      'h5/simulation/config',
      this.basePayload({ roomId }),
    )

    if (config) {
      this._simulationConfig = config
      return config
    }

    throw new Error('No simulation configuration available')
  }

  // ===== RECIPE EXECUTION (HTTP) =====

  executeRecipeAsync(
    recipeId: string,
    inputs?: Record<string, unknown>,
    options?: ExecuteRecipeOptions,
  ): Promise<ExecuteRecipeResponse> {
    return this.callRemote<ExecuteRecipeResponse>('h5/simulation/recipe', this.basePayload({
      recipeId,
      inputs,
      roomId: options?.roomId,
      batchAmount: options?.batchAmount,
      allowPartialBatch: options?.allowPartialBatch,
      entity: options?.entity,
      nonce: options?.nonce,
    }))
  }

  collectRecipeAsync(runId: string): Promise<CollectRecipeResult> {
    return this.callRemote<CollectRecipeResult>('h5/simulation/collect-recipe-rewards', this.basePayload({ runId }))
  }


  resetStateAsync(options?: ResetStateOptions): Promise<ResetStateResult> {
    return this.callRemote<ResetStateResult>('h5/simulation/reset-simulation-and-inventory-state', this.basePayload({
      initializeRecipe: options?.initializeRecipe,
    }))
  }

  getActiveRunsAsync(options?: GetActiveRunsOptions): Promise<SimulationRunSummary[]> {
    return this.callRemote<SimulationRunSummary[]>('h5/simulation/active', this.basePayload({
      roomId: options?.roomId,
    }))
  }

  executeScopedRecipeAsync(
    recipeId: string,
    entity: string,
    inputs?: Record<string, unknown>,
    options?: ExecuteScopedRecipeOptions,
  ): Promise<ExecuteScopedRecipeResult> {
    return this.callRemote<ExecuteScopedRecipeResult>('h5/simulation/scoped-recipe', this.basePayload({
      recipeId,
      entity,
      inputs,
      roomId: options?.roomId ?? null,
      options,
    }))
  }

  getAvailableRecipesAsync(options?: GetAvailableRecipesOptions): Promise<GetAvailableRecipesResult> {
    return this.callRemote<GetAvailableRecipesResult>('h5/simulation/available-recipes', this.basePayload({
      roomId: options?.roomId || null,
      includeActorRecipes: options?.includeActorRecipes || false,
    }))
  }

  getRecipeRequirementsAsync(recipe: Recipe): Promise<RecipeRequirementResult> {
    return this.callRemote<RecipeRequirementResult>('h5/simulation/get-recipe-requirements', this.basePayload({
      recipeId: recipe.recipeId,
      nonce: recipe.nonce,
      entity: recipe.entity,
      batchAmount: recipe.batchAmount,
    }))
  }

  getBatchRecipeRequirementsAsync(recipes: Recipe[]): Promise<GetBatchRecipeRequirements> {
    return this.callRemote<GetBatchRecipeRequirements>('h5/simulation/get-batch-recipe-requirements', this.basePayload({
      recipes,
    }))
  }

  triggerRecipeChainAsync(
    recipeId: string,
    options?: TriggerRecipeChainOptions,
  ): Promise<ExecuteRecipeResponse> {
    return this.callRemote<ExecuteRecipeResponse>('h5/simulation/trigger-recipe-chain', this.basePayload({
      triggerRecipeId: recipeId,
      context: options?.context,
      roomId: options?.roomId,
    }))
  }

  // ===== FIELD RESOLUTION & METADATA (HTTP) =====

  async resolveFieldValueAsync(
    entityId: string,
    fieldPath: string,
    entity?: string,
  ): Promise<unknown> {
    const response = await this.callRemote<{ value: unknown }>(
      'h5/simulation/resolve-field-value',
      this.basePayload({ entityId, fieldPath, entity }),
    )
    return response.value
  }

  getEntityMetadataAsync(entityId: string): Promise<Record<string, unknown>> {
    return this.callRemote<Record<string, unknown>>('h5/simulation/entity-metadata', this.basePayload({ entityId }))
  }

  // ===== SLOT MANAGEMENT (HTTP) =====

  async getSlotContainersAsync(): Promise<SimulationSlotContainer[]> {
    const response = await this.callRemote<{ containers?: SimulationSlotContainer[] }>(
      'h5/simulation/containers',
      this.basePayload(),
    )
    return response.containers || []
  }

  async getSlotAssignmentsAsync(containerId: string): Promise<SimulationAssignment[]> {
    const response = await this.callRemote<
      SimulationAssignment[] | { assignments?: SimulationAssignment[] }
    >('h5/simulation/assignments', this.basePayload({ containerId }))
    return Array.isArray(response) ? response : response.assignments || []
  }

  assignItemToSlotAsync(
    containerId: string,
    slotId: string,
    itemId: string,
  ): Promise<SimulationSlotMutationResult> {
    return this.callRemote<SimulationSlotMutationResult>(
      'h5/simulation/assign',
      this.basePayload({ containerId, slotId, itemId }),
    )
  }

  removeItemFromSlotAsync(containerId: string, slotId: string): Promise<SimulationSlotMutationResult> {
    return this.callRemote<SimulationSlotMutationResult>(
      'h5/simulation/remove',
      this.basePayload({ containerId, slotId }),
    )
  }

  async getAvailableItemsAsync(containerId: string, slotId: string): Promise<SimulationAvailableItem[]> {
    const response = await this.callRemote<{ availableItems?: SimulationAvailableItem[] }>(
      'h5/simulation/available-items-for-slot',
      this.basePayload({ containerId, slotId }),
    )
    return response.availableItems || []
  }

  calculatePowerPreviewAsync(
    containerId: string,
    slotId: string,
    candidateItemId: string,
  ): Promise<SimulationPowerPreview> {
    return this.callRemote<SimulationPowerPreview>(
      'h5/simulation/calculate-power-preview',
      this.basePayload({ containerId, slotId, candidateItemId }),
    )
  }

  validateSlotAssignmentAsync(
    containerId: string,
    slotId: string,
    itemId: string,
  ): Promise<SimulationSlotValidationResult> {
    return this.callRemote<SimulationSlotValidationResult>(
      'h5/simulation/validate-slot-assignment',
      this.basePayload({ containerId, slotId, itemId }),
    )
  }

  executeBatchOperationsAsync(
    operations: Array<SimulationBatchOperation>,
    validateOnly?: boolean,
  ): Promise<SimulationBatchOperationsResult> {
    return this.callRemote<SimulationBatchOperationsResult>(
      'h5/simulation/batch-operations',
      this.basePayload({ operations, validateOnly }),
    )
  }

  // ===== HELPERS =====

  /**
   * Build the base payload for remote calls, including versionTag when set.
   */
  private basePayload(extra?: Record<string, unknown>): Record<string, unknown> {
    const payload: Record<string, unknown> = { appId: this.appId }
    if (this.versionTag) {
      payload.versionTag = this.versionTag
    }
    if (extra) {
      Object.assign(payload, extra)
    }
    return payload
  }

  private ensureValidSubscribeOptions(
    options: unknown,
  ): asserts options is SimulationSubscribeOptions {
    if (typeof options !== 'object' || options === null) {
      throw new Error('Simulation subscribe requires an options object')
    }

    const opts = options as Record<string, unknown>

    if (typeof opts.onUpdate !== 'function') {
      throw new Error('Simulation subscribe requires an onUpdate callback')
    }

    const hasFilter =
      (Array.isArray(opts.entities) && opts.entities.length > 0) ||
      (Array.isArray(opts.tags) && opts.tags.length > 0) ||
      Boolean(opts.activeRuns)

    if (!hasFilter) {
      throw new Error(
        'Simulation subscribe requires at least one filter (entities, tags, activeRuns)',
      )
    }
  }

  destroy(): void {
    for (const unsubscribe of this.activeSubscriptions) {
      unsubscribe()
    }
    this.activeSubscriptions.clear()
  }
}

/**
 * Normalize Firestore Timestamp or epoch milliseconds to Unix timestamp in milliseconds.
 */
function normalizeTimestamp(value: unknown): number {
  if (value === null || value === undefined) {
    return 0
  }

  if (
    typeof value === 'object' &&
    'toMillis' in value &&
    typeof (value as { toMillis: unknown }).toMillis === 'function'
  ) {
    return (value as { toMillis: () => number }).toMillis()
  }

  if (typeof value === 'object' && '_seconds' in value) {
    const ts = value as { _seconds: number; _nanoseconds?: number }
    return ts._seconds * 1000 + Math.floor((ts._nanoseconds ?? 0) / 1_000_000)
  }

  if (typeof value === 'number') {
    return value
  }

  return 0
}
